import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { concatenateName, DateFormatter, TimeFormatter } from "@/utils/functions/helperFunctions";
import { FormTableSkeletonDesktop, FormTableSkeletonMobile } from "@/components/skeletons";
import { useCompactListLayout } from "@/utils/hooks/useCompactListLayout";
import { NO_SEARCH_RESULTS, TABLE_HEADERS, LABELS, GLOBAL_ORGANIZATION } from "@/utils/constants/appConstants/commonConstants";
import { MobileLabelValue } from "@/components/cards/MobileDateField";
import { MobileListChevron } from "@/components/cards/MobileListChevron";
import NoDataAvailable from "@/components/cards/NoDataAvailable";
import NoFilterDataCard from "@/components/cards/NoFilterDataCard";
import TransferOwnershipModal from "@/components/modals/TransferOwnershipModal";
import TransferOwnershipButton from "@/components/buttons/TransferOwnershipButton";
import { transferFormOwnership } from "@/services/form.service";
import { isFeaturePermitted } from "@/utils/functions/rbac";
import { getCurrentOrganization } from "@/utils/functions/OrganizationFunctions";

interface FormTableProps {
    forms: any;
    loadingData: boolean;
    setForms: any;
    dataLength: number;
    sorting: string;
    isLoading: boolean;
    allFetched?: boolean;
    setSorting: ({}) => void;
    search: (searchText: string) => void;
    reset: () => void;
    searchItem: string | null;
    title: string;
    dataLoad: boolean;
    resetDataQueries: () => void;
    setPageNo: (pageNo: number) => void;
    id: string;
}

function FormTable(props: FormTableProps) {
    const navigate = useNavigate();
    const isCompactLayout = useCompactListLayout();
    const [subjectStatus, setSubjectStatus] = useState(true);
    const [updatedAtStatus, setUpdatedAtStatus] = useState(true);
    const [sortElement, setSortElement] = React.useState("created_at");
    
    // Transfer ownership states
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [selectedForm, setSelectedForm] = useState<any>(null);
    const [transferLoading, setTransferLoading] = useState(false);
    const [isTransferFormOwnershipPermitted, setIsTransferFormOwnershipPermitted] = useState(false);

    const currentOrganization = getCurrentOrganization();

    // Check permissions on component mount
    React.useEffect(() => {
        setIsTransferFormOwnershipPermitted(isFeaturePermitted('AdminPortalTransferFormOwnership'));
    }, []);

    // Handle opening transfer ownership modal
    const handleOpenTransferModal = (form: any) => {
        setTransferLoading(false);
        setSelectedForm(form);
        setShowTransferModal(true);
    };

    const handleTransferOwnership = async (selectedUser: any) => {
        if (!selectedUser || !selectedForm) return;
        
        setTransferLoading(true);
        try {
            const payload: { user_id?: string; email_address?: string } = {};
            
            if (selectedUser.user?.id) {
                payload.user_id = selectedUser.user.id;
            } else if (selectedUser.user?.email_address) {
                payload.email_address = selectedUser.user.email_address;
            } else {
                throw new Error('No valid user identifier found');
            }
            await transferFormOwnership(selectedForm.id, payload);
            setShowTransferModal(false);
            setSelectedForm(null);
            props.setPageNo(1);
            props.resetDataQueries();
            
        } catch (error) {
            console.log('Error transferring form ownership:', error);
        } finally {
            setTransferLoading(false);
        }
    };

    const renderFormStatusBadge = (form: any, badgeId?: string) => (
        <span
            id={badgeId}
            className={`badge badge-sm badge-dim text-capitalize gs-form-table-status-badge ${
                form?.is_accepting_responses ? "badge-outline-success" : "badge-outline-secondary"
            }`}
        >
            {form?.is_accepting_responses ? "Active" : "Inactive"}
        </span>
    );

    return (
        <>
            {isCompactLayout ? (
                props.forms && !props.dataLoad ? (
                    props.forms?.length > 0 ? (
                        <div className="envelope_table">
                            {props.forms?.map((form: any, key: number) => (
                                <div className="envelope_tr gs-mobile-list-card" key={form.id}>
                                    <div className="gs-mobile-card-icon">
                                        <em className="icon ni ni-notes-alt icons" id={`${props.id}-icon`}></em>
                                    </div>
                                    <div className="env_meta_data gs-mobile-list-card__content w-100" id={`${props.id}-subject-${key}-mb`}>
                                        {form.name}
                                        <div className="text-dark">
                                            <span className="fw-bold">Template: </span>
                                            <span className="tb-lead-sub" id={`${props.id}-created-by-${key}-mb`}>
                                                {form.template?.id ? (
                                                    <Link className="link link-primary link-sm" to={"/template-details/" + form.template.id}>
                                                        {form.template?.name}
                                                    </Link>
                                                ) : (
                                                    form.template?.name || "—"
                                                )}
                                            </span>
                                        </div>
                                        <MobileLabelValue
                                            label="Responses: "
                                            value={`${form?.total_responses ? form?.total_responses : "0"}${form?.configurations?.max_allowed_responses === 10000 ? "" : `/${form?.configurations?.max_allowed_responses}`}`}
                                        />
                                        <MobileLabelValue
                                            label={`${TABLE_HEADERS.STATUS}: `}
                                            value={renderFormStatusBadge(form)}
                                            id={`${props.id}-status-${key}-mb`}
                                        />
                                        <MobileLabelValue
                                            label={`${TABLE_HEADERS.SENDER}: `}
                                            value={concatenateName(form.created_by?.first_name, form.created_by?.last_name)}
                                        />
                                        {isTransferFormOwnershipPermitted &&
                                        currentOrganization?.name !== GLOBAL_ORGANIZATION ? (
                                            <div className="gs-mobile-transfer-ownership">
                                                <TransferOwnershipButton
                                                    id={`form-transfer-ownership-btn-${key}-mb`}
                                                    iconId={`form-transfer-ownership-icon-${key}-mb`}
                                                    onClick={() => handleOpenTransferModal(form)}
                                                />
                                            </div>
                                        ) : null}
                                    </div>
                                    <MobileListChevron
                                        id={`${props.id}-redirect-${key}-mb`}
                                        onClick={() => navigate(`/form-details/${form.id}`)}
                                    />
                                </div>
                            ))}
                        </div>
                    ) : props.searchItem ? (
                        <NoDataAvailable>
                            <p id={`${props.id}-no-search-result`}>{NO_SEARCH_RESULTS}</p>
                        </NoDataAvailable>
                    ) : (
                        <NoDataAvailable>
                            <NoFilterDataCard id={props.id} loadAll={props.resetDataQueries} />
                        </NoDataAvailable>
                    )
                ) : (
                    <FormTableSkeletonMobile
                        showActionColumn={
                            isTransferFormOwnershipPermitted &&
                            currentOrganization?.name !== GLOBAL_ORGANIZATION
                        }
                    />
                )
            ) : (
                <table
                    className={`nk-tb-list nk-tb-ulist table-weight gs-form-table${isTransferFormOwnershipPermitted && currentOrganization?.name !== GLOBAL_ORGANIZATION ? " gs-form-table--with-actions" : ""}`}
                    style={{ tableLayout: "fixed", width: "100%" }}
                >
                    <thead>
                        <tr className="nk-tb-item nk-tb-head">
                    <th className="nk-tb-col gs-form-col-name">
                        <span id={`${props.id}-name-th`}>{TABLE_HEADERS.NAME}</span>
                    </th>
                    <th className="nk-tb-col gs-form-col-responses">
                        <span id={`${props.id}-responses-th`}>{TABLE_HEADERS.RESPONSES}</span>
                    </th>
                    <th className="nk-tb-col gs-form-col-status">
                        <span id={`${props.id}-status-th`}>{TABLE_HEADERS.STATUS}</span>
                    </th>
                    <th className="nk-tb-col gs-form-col-owner">
                        <span id={`${props.id}-owner-th`}>{TABLE_HEADERS.OWNER}</span>
                    </th>
                    {isTransferFormOwnershipPermitted && (currentOrganization?.name !== GLOBAL_ORGANIZATION) && (
                        <th className="nk-tb-col gs-form-col-actions">
                            <span id={`${props.id}-actions-th`}>{TABLE_HEADERS.ACTIONS}</span>
                        </th>
                    )}
                        </tr>
                    </thead>
                    {props.forms && !props.dataLoad ? (
                        props.forms?.length > 0 ? (
                            <tbody>
                                {props.forms.map((form: any, key: number) => (
                                    <tr className="nk-tb-item" key={key}>
                                        <td className="nk-tb-col subject-col">
                                            <Link to={"/form-details/" + form?.id} className="project-title">
                                                <div className="icon mr-2 fs-5 mt-1">
                                                    <em className="icon ni ni-notes-alt text-gray"></em>
                                                </div>
                                                <div className="data" style={{ marginTop: 4, maxWidth: "80%", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                                                    <div className="info">
                                                        <div className="text-dark name tb-col-md">{form.name}</div>
                                                        <div className="meta">
                                                            <span className="version">
                                                                {form.template?.id ? (
                                                                    <Link className="link link-primary link-sm" to={"/template-details/" + form.template.id}>
                                                                        {form.template?.name}
                                                                    </Link>
                                                                ) : (
                                                                    form.template?.name || "—"
                                                                )}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </Link>
                                        </td>
                                        <td className="nk-tb-col gs-form-col-responses tb-col-mb text-left">
                                            <div className="meta">
                                                <span className="text-dark">{`${form?.total_responses ? form?.total_responses : "0"}${form?.configurations?.max_allowed_responses === 10000 ? "" : `/${form?.configurations?.max_allowed_responses}`}`}</span>
                                            </div>
                                        </td>
                                        <td className="nk-tb-col gs-form-col-status tb-col-mb">
                                            {renderFormStatusBadge(form, `${props.id}-status-${key}`)}
                                        </td>
                                        <td className="nk-tb-col gs-form-col-owner tb-col-mb text-left">
                                            <div className="meta">
                                                <span className="text-dark">
                                                    {concatenateName(form.owner?.first_name, form.owner?.last_name)}
                                                </span>
                                            </div>
                                        </td>
                                {isTransferFormOwnershipPermitted && (currentOrganization?.name !== GLOBAL_ORGANIZATION) && (
                                    <td className="nk-tb-col gs-form-col-actions">
                                        <TransferOwnershipButton
                                            id={`form-transfer-ownership-btn-${key}`}
                                            iconId={`form-transfer-ownership-icon-${key}`}
                                            onClick={() => handleOpenTransferModal(form)}
                                        />
                                    </td>
                                )}
                                    </tr>
                                ))}
                        {props.isLoading && <FormTableSkeletonDesktop rowCount={2} showActionColumn={isTransferFormOwnershipPermitted && (currentOrganization?.name !== GLOBAL_ORGANIZATION)} />}
                    </tbody>
                ) : props.searchItem ? (
                    <tbody>
                        <tr>
                            <td colSpan={isTransferFormOwnershipPermitted ? 5 : 4}>
                                <NoDataAvailable>
                                    <p id={`${props.id}-no-search-result`}>{NO_SEARCH_RESULTS}</p>
                                </NoDataAvailable>
                            </td>
                        </tr>
                    </tbody>
                ) : (
                    <tbody>
                        <tr>
                            <td colSpan={isTransferFormOwnershipPermitted ? 5 : 4}>
                                <NoDataAvailable>
                                    <NoFilterDataCard id={props.id} loadAll={props.resetDataQueries} />
                                </NoDataAvailable>
                            </td>
                        </tr>
                    </tbody>
                )
            ) : (
                <tbody>
                    <FormTableSkeletonDesktop showActionColumn={isTransferFormOwnershipPermitted && (currentOrganization?.name !== GLOBAL_ORGANIZATION)} />
                </tbody>
            )}
                </table>
            )}
            
        {/* Transfer Ownership Modal */}
        {selectedForm && (
            <TransferOwnershipModal
                show={showTransferModal}
                onHide={() => {
                    setShowTransferModal(false);
                    setSelectedForm(null);
                }}
                onTransferOwnership={handleTransferOwnership}
                loading={transferLoading}
                currentOwnerId={selectedForm?.owner?.id}
                title={LABELS.TRANSFER_FORM_OWNERSHIP}
                description={LABELS.TRANSFER_FORM_OWNERSHIP_DESCRIPTION}
            />
        )}
        </>
    );
}

export default FormTable;
